import * as React from 'react';
import { ForwardRefWithStaticComponents } from '@/types';
import { classNames, useUid } from '@/utils';
import { CheckboxGroup } from './CheckboxGroup/CheckboxGroup';
import { useCheckboxGroupContext } from './CheckboxGroup/CheckboxGroup.context';

export interface CheckboxProps {
  /** The HTML ID attribute to be applied to the checkbox input element (optional). */
  id?: string;
  /** The name attribute to be applied to the checkbox input element (optional). */
  name?: string;
  /**
   * The value attribute to be applied to the checkbox input element (required).
   * This value is submitted with the form data when the checkbox is checked.
   */
  value: string;
  /** Whether the checkbox input should be checked (optional). */
  checked?: boolean;
  /** Whether the checkbox input should be disabled (optional). */
  disabled?: boolean;
  /** The label to be displayed next to the checkbox input (optional). */
  label?: React.ReactNode;
  /** CSS class names to be applied to the label element (optional). */
  labelClassName?: string;
  /** CSS class names to be applied to the checkbox input element (optional). */
  inputClassName?: string;
  /** Whether the checkbox should be displayed as a rich selector with additional elements (optional). */
  isRichSelector?: boolean;

  /**
   * A function to be called when the value of the checkbox input changes (optional).
   * The function receives the new status of the checkbox as a boolean value.
   */
  onChange?(status: boolean): void;
}

type CheckboxComponent = ForwardRefWithStaticComponents<CheckboxProps, { Group: typeof CheckboxGroup }>;

export const Checkbox: CheckboxComponent = React.forwardRef<HTMLInputElement, CheckboxProps>((props, ref) => {
  const { id, label, name, disabled, value, labelClassName, inputClassName, isRichSelector, checked, onChange } = props;
  const ctx = useCheckboxGroupContext();

  const uid = useUid(id);

  const contextProps = ctx
    ? {
        checked: ctx.value.includes(value as string),
        onChange: ctx.onChange,
      }
    : {
        checked,
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
          onChange?.(event.target.checked);
        },
      };

  return (
    <label
      htmlFor={uid}
      className={classNames(labelClassName, { selector: isRichSelector, selected: contextProps.checked })}
    >
      <input
        value={value}
        ref={ref}
        type="checkbox"
        name={name}
        id={uid}
        disabled={disabled}
        checked={contextProps.checked}
        onChange={contextProps.onChange}
        className={inputClassName}
      />
      <span className={classNames('input-checkbox', inputClassName)} />
      <div className="content">{label}</div>
    </label>
  );
}) as any;

Checkbox.displayName = 'Checkbox';
Checkbox.Group = CheckboxGroup;
